import { useCallback, useEffect, useReducer, useRef } from "react";

import {
  DEFAULT_PEEK_TIMING,
  IDLE_PEEK,
  isEmptyZone,
  reducePeek,
  type PeekEvent,
  type PeekState,
  type PeekTiming,
} from "../lib/cheat-peek";

export interface CheatPeek {
  /** Vrai tant que le maintien dure. */
  peeking: boolean;
  handlers: {
    onPointerDown: (event: React.PointerEvent) => void;
    onPointerMove: (event: React.PointerEvent) => void;
    onPointerUp: (event: React.PointerEvent) => void;
    onPointerCancel: () => void;
  };
}

/**
 * Le geste se lit en `PointerEvent` et non en `touch` : il doit valoir au doigt
 * comme à la souris, et `zoom-lock` ne neutralise que les `gesture*` d'iOS,
 * sans jamais toucher à ce flux. Toute la reconnaissance vit dans
 * `lib/cheat-peek.ts` ; il ne reste ici que la minuterie du maintien et les
 * raccrochages qui referment le coup d'œil.
 */
export const useCheatPeek = (
  { enabled }: { enabled: boolean },
  timing: PeekTiming = DEFAULT_PEEK_TIMING,
): CheatPeek => {
  const [state, dispatch] = useReducer(
    (current: PeekState, event: PeekEvent) => reducePeek(current, event, timing),
    IDLE_PEEK,
  );
  const holdTimer = useRef<number | null>(null);

  const clearHold = useCallback(() => {
    if (holdTimer.current !== null) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  }, []);

  // La minuterie suit l'état plutôt que les gestionnaires : c'est la machine
  // qui décide qu'un maintien court, et un seul endroit l'arme ou l'éteint.
  useEffect(() => {
    if (!state.holding) {
      clearHold();
      return;
    }
    holdTimer.current = window.setTimeout(() => {
      holdTimer.current = null;
      dispatch({ type: "hold-elapsed" });
    }, timing.holdDelay);
    return clearHold;
  }, [clearHold, state.holding, timing.holdDelay]);

  // Un geste ne survit ni à la fin de la partie ni à un onglet qu'on quitte le
  // doigt posé : sans cela les cases resteraient retournées au retour.
  useEffect(() => {
    if (!enabled) dispatch({ type: "abandon" });
  }, [enabled]);

  useEffect(() => {
    if (!state.peeking) return;
    const drop = () => dispatch({ type: "abandon" });
    window.addEventListener("blur", drop);
    document.addEventListener("visibilitychange", drop);
    return () => {
      window.removeEventListener("blur", drop);
      document.removeEventListener("visibilitychange", drop);
    };
  }, [state.peeking]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (!enabled) return;
      dispatch({
        type: "down",
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        at: event.timeStamp,
        emptyZone: isEmptyZone(event.target),
      });
    },
    [enabled],
  );

  const onPointerMove = useCallback((event: React.PointerEvent) => {
    dispatch({ type: "move", pointerId: event.pointerId, x: event.clientX, y: event.clientY });
  }, []);

  const onPointerUp = useCallback((event: React.PointerEvent) => {
    dispatch({ type: "up", pointerId: event.pointerId, at: event.timeStamp });
  }, []);

  const onPointerCancel = useCallback(() => dispatch({ type: "abandon" }), []);

  return {
    peeking: state.peeking,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
  };
};
